import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import axiosInstance from "../utils/api";
import toast from "react-hot-toast";
import { FiUser, FiMail, FiPhone, FiCreditCard, FiSearch } from "react-icons/fi";
import { BsShop } from "react-icons/bs";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const ContactorModal = ({ isOpen, onClose, eventId, contractor, onSuccess }) => {
  const [stands, setStands] = useState([]);
  const [selectedStands, setSelectedStands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    cin: "",
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchStands = async () => {
      try {
        const res = await axiosInstance.get(`/stand/${eventId}`);
        setStands(res.data.stands || []);
      } catch (err) {
        toast.error("Failed to load stands");
      }
    };

    if (isOpen && eventId) fetchStands();
  }, [isOpen, eventId]);

  useEffect(() => {
    if (contractor) {
      setForm({
        name: contractor.name || "",
        email: contractor.email || "",
        phone: contractor.phone || "",
        cin: contractor.cin || "",
      });
      setSelectedStands(contractor.stands?.map(s => s._id || s) || []);
    } else {
      setForm({ name: "", email: "", phone: "", cin: "" });
      setSelectedStands([]);
    }
    setSearchTerm("");
  }, [contractor, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.cin) {
      toast.error("CIN is required");
      return;
    }

    try {
      setSubmitting(true);

      if (contractor) {
        await axiosInstance.put(`/auth/user/${contractor._id}`, {
          ...form,
          stands: selectedStands,
        });
        toast.success("Contractor updated successfully");
      } else {
        await axiosInstance.post("/auth/register", {
          ...form,
          role: "contractor",
          event: eventId,
          stands: selectedStands,
        });
        toast.success("Contractor created successfully");
      }

      onClose();

      try {
        onSuccess();
      } catch (err) {
        console.error("onSuccess callback failed:", err);
      }

    } catch (e) {
      toast.error(e?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={contractor ? "Edit Contractor" : "Add New Contractor"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
            <FiUser className="w-4 h-4" />
            Personal Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${contractor ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  placeholder="email@example.com"
                  type="email"
                  value={form.email}
                  disabled={!!contractor}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              {contractor && (
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                CIN <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiCreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter CIN"
                  value={form.cin}
                  onChange={(e) => setForm({ ...form, cin: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone Number
              </label>
              <PhoneInput
                placeholder="Enter phone number (optional)"
                value={form.phone}
                onChange={(value) => setForm({ ...form, phone: value ?? '' })}
                defaultCountry="MA"
                international
                inputClassName="w-full pl-4 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                className="w-full pl-4 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <span>{contractor ? 'Update Contractor' : 'Create Contractor'}</span>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ContactorModal;